import { create } from 'zustand';
import { mutative } from 'zustand-mutative';

import { LS_KEYS } from '@/keys/ls_keys.ts';
import type { LocaleName, Setting } from '@/models';

export const useSetting = create<Setting>()(
  mutative(() =>
    JSON.parse(
      localStorage.getItem(LS_KEYS.SETTING) ??
        JSON.stringify({ themeName: 'auto', localeName: 'auto' as const }),
    ),
  ),
);

export function setSetting(setting: Partial<Setting>) {
  useSetting.setState((state) => {
    Object.assign(state, setting);
    localStorage.setItem(LS_KEYS.SETTING, JSON.stringify(state));
  });
}

export function useThemeName() {
  const sysThemeName = useSystemSetting((state) => state.themeName);
  const appThemeName = useSetting((state) => state.themeName);
  if (appThemeName === 'auto') {
    return sysThemeName;
  } else {
    return appThemeName;
  }
}

export function getLocaleName() {
  const sysLocaleName = useSystemSetting.getState().localeName;
  const appLocaleName = useSetting.getState().localeName;
  if (appLocaleName === 'auto') {
    return sysLocaleName;
  } else {
    return appLocaleName;
  }
}
export function useLocaleName() {
  const sysLocaleName = useSystemSetting((state) => state.localeName);
  const appLocaleName = useSetting((state) => state.localeName);
  if (appLocaleName === 'auto') {
    return sysLocaleName;
  } else {
    return appLocaleName;
  }
}

// System settings
export const useSystemSetting = create<{
  localeName: LocaleName;
  themeName: 'light' | 'dark';
}>()(
  mutative(() => {
    return {
      localeName: getSysLocale(),
      themeName: getSysTheme(),
    };
  }),
);

// Listen for system theme changes
window
  .matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', () => {
    useSystemSetting.setState((state) => {
      state.themeName = getSysTheme();
    });
  });

// Listen for system language changes
window.addEventListener('languagechange', () => {
  useSystemSetting.setState((state) => {
    state.localeName = getSysLocale();
  });
});

export function getSysLocale(): LocaleName {
  return navigator.language.includes('zh') ? 'zh-CN' : 'en-GB';
}

export function getSysTheme() {
  return window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : ('light' as 'light' | 'dark');
}
